import type { Game, Goals, Metric, Opponent, Player, PlayerStats, Serve } from "../types";
import {
  aggregateStats,
  formatSpeed,
  makePlayerStats,
  previousDateRange,
  serveTypeLabel,
  speedForMetric
} from "./analytics";
import { displayScale, gameStats, onScale, receiverStats } from "./games";
import { t } from "../i18n";

export type ReportFormat = "csv" | "html" | "pdf";
export type ReportScope = "all" | "top" | "risk";
export type ReportTemplate = "summary" | "match" | "player" | "season";

export type ReportDateRange = { start?: string; end?: string };

export type ReportOptions = {
  format: ReportFormat;
  scope: ReportScope;
  template: ReportTemplate;
  dateRange: ReportDateRange;
  metric: Metric;
  goals: Goals;
  players: Player[];
  serves: Serve[];
  games: Game[];
  opponents: Opponent[];
};

export type GeneratedReport = {
  filename: string;
  mimeType: string;
  extension: ReportFormat;
  blob: Blob;
  text?: string;
  title: string;
};

export type SavedReport = {
  id: string;
  name: string;
  createdAt: string;
  status: "ready";
  format: ReportFormat;
  scope: ReportScope;
  template: ReportTemplate;
  dateRange: ReportDateRange;
  metric: Metric;
  filename: string;
  mimeType: string;
  /** CSV/HTML text, or data-URL for PDF */
  payload: string;
};

const HISTORY_LIMIT = 12;

export const REPORT_TEMPLATES: Array<{
  id: ReportTemplate;
  name: string;
  detail: string;
  defaultScope: ReportScope;
}> = [
  { id: "match", name: "Match prep", detail: "Serve mix, risk bands, target gaps", defaultScope: "risk" },
  { id: "player", name: "Player review", detail: "Individual speed, outcomes, trends", defaultScope: "all" },
  { id: "season", name: "Season packet", detail: "Leaderboard and period comparisons", defaultScope: "all" }
];

export function scopeLabel(scope: ReportScope) {
  if (scope === "top") return "Top servers";
  if (scope === "risk") return "Risk review";
  return "All players";
}

export function templateLabel(template: ReportTemplate) {
  return REPORT_TEMPLATES.find((entry) => entry.id === template)?.name ?? "Summary";
}

export function periodLabel(dateRange: ReportDateRange) {
  if (dateRange.start || dateRange.end) {
    return `${dateRange.start || "First serve"} – ${dateRange.end || "Latest"}`;
  }
  return "All time";
}

function historyKey(uid: string) {
  return `serveboost-reports-${uid}`;
}

export function loadReportHistory(uid: string): SavedReport[] {
  try {
    const raw = localStorage.getItem(historyKey(uid));
    if (!raw) return [];
    const parsed = JSON.parse(raw) as SavedReport[];
    return Array.isArray(parsed) ? parsed : [];
  } catch {
    return [];
  }
}

export function saveReportHistory(uid: string, reports: SavedReport[]) {
  localStorage.setItem(historyKey(uid), JSON.stringify(reports.slice(0, HISTORY_LIMIT)));
}

export function pushReportHistory(uid: string, report: SavedReport) {
  const next = [report, ...loadReportHistory(uid).filter((entry) => entry.id !== report.id)].slice(0, HISTORY_LIMIT);
  saveReportHistory(uid, next);
  return next;
}

export function removeReportHistory(uid: string, id: string) {
  const next = loadReportHistory(uid).filter((entry) => entry.id !== id);
  saveReportHistory(uid, next);
  return next;
}

export function getReportStats(players: Player[], serves: Serve[], dateRange: ReportDateRange, scope: ReportScope) {
  const stats = makePlayerStats(players, serves, dateRange);
  if (scope === "top") return stats.slice(0, 5);
  if (scope === "risk") return [...stats].sort((a, b) => b.misses - a.misses || b.totalServes - a.totalServes).slice(0, 5);
  return stats;
}

function missRate(stat: PlayerStats) {
  return stat.totalServes ? (stat.misses / stat.totalServes) * 100 : 0;
}

function csvEscape(cell: string | number) {
  return `"${String(cell).replace(/"/g, "\"\"")}"`;
}

function csvBlock(rows: Array<Array<string | number>>) {
  return rows.map((row) => row.map(csvEscape).join(",")).join("\n");
}

function typeName(type: string) {
  return serveTypeLabel(type);
}

function goalEfficiency(goals: Goals, type: string) {
  if (type === "float") return goals.effiencyFloat;
  if (type === "hyb") return goals.effiencyHybrid;
  return goals.effiencySpin;
}

function goalSpeed(goals: Goals, type: string) {
  if (type === "float") return goals.avgSpeedFloat;
  if (type === "hyb") return goals.avgSpeedHybrid;
  return goals.avgSpeedSpin;
}

type ReportModel = {
  title: string;
  generatedAt: string;
  scope: ReportScope;
  template: ReportTemplate;
  metric: Metric;
  dateRange: ReportDateRange;
  period: string;
  previousPeriod: string;
  stats: PlayerStats[];
  team: PlayerStats;
  previousTeam: PlayerStats;
  goals: Goals;
  mix: Array<{
    type: string;
    label: string;
    total: number;
    efficiency: number;
    avgSpeed: number;
    efficiencyGap: number;
    speedGap: number;
  }>;
  riskBand: PlayerStats[];
  leaders: {
    efficiency: PlayerStats[];
    aces: PlayerStats[];
    speed: PlayerStats[];
  };
  playerTrends: Array<{
    name: string;
    efficiency: number;
    efficiencyDelta: number;
    avgSpeed: number;
    speedDelta: number;
    serves: number;
  }>;
  games: Array<{
    date: string;
    opponent: string;
    serves: number;
    aceRate: number;
    missRate: number;
    avgGrade: string;
    avgSpeed: number;
  }>;
  targets: Array<{ number: number; avgGrade: string; graded: number; avgSpeed: number }>;
  gradeScale: string;
};

function buildModel(options: ReportOptions): ReportModel {
  const { players, serves, goals, games, opponents, scope, template, dateRange, metric } = options;
  const stats = getReportStats(players, serves, dateRange, scope);
  const allInRange = makePlayerStats(players, serves, dateRange);
  const team = aggregateStats(allInRange);
  const previous = previousDateRange(dateRange, serves);
  const previousStats = makePlayerStats(players, serves, previous);
  const previousTeam = aggregateStats(previousStats);
  const previousById = new Map(previousStats.map((stat) => [stat.id, stat]));

  const mix = (["float", "hyb", "spin"] as const).map((type) => {
    const bucket = team.byType[type];
    const avgSpeed = speedForMetric(bucket?.avgSpeed ?? 0, metric);
    const goalAvg = speedForMetric(goalSpeed(goals, type), metric);
    return {
      type,
      label: typeName(type),
      total: bucket?.total ?? 0,
      efficiency: bucket?.efficiency ?? 0,
      avgSpeed,
      efficiencyGap: (bucket?.efficiency ?? 0) - goalEfficiency(goals, type),
      speedGap: avgSpeed - goalAvg
    };
  });

  const riskBand = [...allInRange]
    .filter((stat) => stat.totalServes > 0)
    .sort((a, b) => missRate(b) - missRate(a) || b.misses - a.misses)
    .slice(0, 8);

  const leaders = {
    efficiency: [...allInRange].filter((s) => s.totalServes).sort((a, b) => b.efficiency - a.efficiency).slice(0, 5),
    aces: [...allInRange].filter((s) => s.totalServes).sort((a, b) => b.aces - a.aces).slice(0, 5),
    speed: [...allInRange].filter((s) => s.totalServes).sort((a, b) => b.avgSpeed - a.avgSpeed).slice(0, 5)
  };

  const playerTrends = stats.map((stat) => {
    const prior = previousById.get(stat.id);
    return {
      name: stat.name,
      efficiency: stat.efficiency,
      efficiencyDelta: prior ? stat.efficiency - prior.efficiency : 0,
      avgSpeed: speedForMetric(stat.avgSpeed, metric),
      speedDelta: prior ? speedForMetric(stat.avgSpeed - prior.avgSpeed, metric) : 0,
      serves: stat.totalServes
    };
  });

  const rangeStart = dateRange.start ? new Date(`${dateRange.start}T00:00:00`) : null;
  const rangeEnd = dateRange.end ? new Date(`${dateRange.end}T23:59:59`) : null;
  const scopedGames = games.filter((game) => {
    const played = new Date(`${game.playedOn}T12:00:00`);
    if (rangeStart && played < rangeStart) return false;
    if (rangeEnd && played > rangeEnd) return false;
    return true;
  });
  const gameServes = serves.filter((serve) => serve.gameId && scopedGames.some((game) => game.id === serve.gameId));
  const scale = displayScale(gameServes);
  const perGame = gameStats(gameServes, scopedGames, opponents)
    .sort((a, b) => b.game.playedOn.localeCompare(a.game.playedOn))
    .slice(0, 8)
    .map((row) => ({
      date: row.game.playedOn,
      opponent: row.opponent,
      serves: row.serves,
      aceRate: row.aceRate,
      missRate: row.missRate,
      avgGrade: row.avgGrade == null ? "—" : onScale(row.avgGrade, scale).toFixed(2),
      avgSpeed: speedForMetric(row.avgSpeed, metric)
    }));

  const targets = receiverStats(gameServes)
    .filter((row) => row.graded >= 3)
    .slice(0, 5)
    .map((row) => ({
      number: row.number,
      avgGrade: row.avgGrade == null ? "—" : onScale(row.avgGrade, scale).toFixed(2),
      graded: row.graded,
      avgSpeed: speedForMetric(row.avgSpeed, metric)
    }));

  return {
    title: `${templateLabel(template)} · ${scopeLabel(scope)}`,
    generatedAt: new Date().toLocaleString(),
    scope,
    template,
    metric,
    dateRange,
    period: periodLabel(dateRange),
    previousPeriod: periodLabel(previous),
    stats,
    team,
    previousTeam,
    goals,
    mix,
    riskBand,
    leaders,
    playerTrends,
    games: perGame,
    targets,
    gradeScale: `${scale[0]}–${scale[1]}`
  };
}

function delta(value: number, digits = 1) {
  const sign = value > 0 ? "+" : "";
  return `${sign}${value.toFixed(digits)}`;
}

function buildCsv(model: ReportModel) {
  const blocks: string[] = [];
  blocks.push(csvBlock([
    ["ServeBoost Report"],
    ["Title", model.title],
    ["Generated", model.generatedAt],
    ["Period", model.period],
    ["Metric", model.metric],
    ["Scope", scopeLabel(model.scope)],
    ["Template", templateLabel(model.template)]
  ]));

  blocks.push("");
  blocks.push(csvBlock([
    ["Team KPI", "Value"],
    ["Serves", model.team.totalServes],
    ["Efficiency %", model.team.efficiency.toFixed(1)],
    ["Avg speed", speedForMetric(model.team.avgSpeed, model.metric).toFixed(1)],
    ["Max speed", speedForMetric(model.team.maxSpeed, model.metric).toFixed(1)],
    ["Miss rate %", model.team.totalServes ? ((model.team.misses / model.team.totalServes) * 100).toFixed(1) : "0.0"]
  ]));

  blocks.push("");
  blocks.push(csvBlock([
    ["Player", "Position", "Serves", "Aces", "Passes", "Misses", "Efficiency %", `Avg ${model.metric}`, `Max ${model.metric}`],
    ...model.stats.map((stat) => [
      stat.name,
      stat.position,
      stat.totalServes,
      stat.aces,
      stat.passes,
      stat.misses,
      stat.efficiency.toFixed(1),
      speedForMetric(stat.avgSpeed, model.metric).toFixed(1),
      speedForMetric(stat.maxSpeed, model.metric).toFixed(1)
    ])
  ]));

  if (model.template === "match" || model.template === "summary") {
    blocks.push("");
    blocks.push(csvBlock([
      ["Serve mix", "Serves", "Efficiency %", `Avg ${model.metric}`, "Eff gap vs goal", "Speed gap vs goal"],
      ...model.mix.map((row) => [
        row.label,
        row.total,
        row.efficiency.toFixed(1),
        row.avgSpeed.toFixed(1),
        delta(row.efficiencyGap),
        delta(row.speedGap)
      ])
    ]));

    blocks.push("");
    blocks.push(csvBlock([
      ["Risk band", "Misses", "Miss rate %", "Serves", "Efficiency %"],
      ...model.riskBand.map((stat) => [
        stat.name,
        stat.misses,
        missRate(stat).toFixed(1),
        stat.totalServes,
        stat.efficiency.toFixed(1)
      ])
    ]));
  }

  if (model.template === "player" || model.template === "season") {
    blocks.push("");
    blocks.push(csvBlock([
      ["Player trends vs prior period", "Serves", "Efficiency %", "Eff Δ", `Avg ${model.metric}`, "Speed Δ"],
      ...model.playerTrends.map((row) => [
        row.name,
        row.serves,
        row.efficiency.toFixed(1),
        delta(row.efficiencyDelta),
        row.avgSpeed.toFixed(1),
        delta(row.speedDelta)
      ])
    ]));
  }

  if (model.template === "season") {
    blocks.push("");
    blocks.push(csvBlock([
      ["Period comparison", "Current", "Prior", "Δ"],
      ["Period", model.period, model.previousPeriod, ""],
      ["Serves", model.team.totalServes, model.previousTeam.totalServes, model.team.totalServes - model.previousTeam.totalServes],
      ["Efficiency %", model.team.efficiency.toFixed(1), model.previousTeam.efficiency.toFixed(1), delta(model.team.efficiency - model.previousTeam.efficiency)],
      [
        `Avg ${model.metric}`,
        speedForMetric(model.team.avgSpeed, model.metric).toFixed(1),
        speedForMetric(model.previousTeam.avgSpeed, model.metric).toFixed(1),
        delta(speedForMetric(model.team.avgSpeed - model.previousTeam.avgSpeed, model.metric))
      ]
    ]));

    blocks.push("");
    blocks.push(csvBlock([
      ["Leaderboard · efficiency", "Efficiency %", "Serves"],
      ...model.leaders.efficiency.map((stat) => [stat.name, stat.efficiency.toFixed(1), stat.totalServes])
    ]));
    blocks.push("");
    blocks.push(csvBlock([
      ["Leaderboard · aces", "Aces", "Serves"],
      ...model.leaders.aces.map((stat) => [stat.name, stat.aces, stat.totalServes])
    ]));
    blocks.push("");
    blocks.push(csvBlock([
      [`Leaderboard · avg ${model.metric}`, `Avg ${model.metric}`, "Serves"],
      ...model.leaders.speed.map((stat) => [stat.name, speedForMetric(stat.avgSpeed, model.metric).toFixed(1), stat.totalServes])
    ]));
  }

  if (model.template === "match" && (model.games.length || model.targets.length)) {
    if (model.games.length) {
      blocks.push("");
      blocks.push(csvBlock([
        ["Recent games", "Opponent", "Serves", "Ace %", "Miss %", `Pass avg (${model.gradeScale})`, `Avg ${model.metric}`],
        ...model.games.map((game) => [
          game.date,
          game.opponent,
          game.serves,
          game.aceRate.toFixed(1),
          game.missRate.toFixed(1),
          game.avgGrade,
          game.avgSpeed.toFixed(1)
        ])
      ]));
    }
    if (model.targets.length) {
      blocks.push("");
      blocks.push(csvBlock([
        ["Serve targets (weakest passers)", `Pass avg (${model.gradeScale})`, "Graded", `Avg ${model.metric}`],
        ...model.targets.map((target) => [`#${target.number}`, target.avgGrade, target.graded, target.avgSpeed.toFixed(1)])
      ]));
    }
  }

  if (model.template === "player") {
    blocks.push("");
    const typeRows: Array<Array<string | number>> = [["Player serve types", "Type", "Serves", "Aces", "Misses", "Efficiency %", `Avg ${model.metric}`]];
    for (const stat of model.stats) {
      for (const type of ["float", "hyb", "spin"]) {
        const bucket = stat.byType[type];
        if (!bucket?.total) continue;
        typeRows.push([
          stat.name,
          typeName(type),
          bucket.total,
          bucket.aces,
          bucket.misses,
          bucket.efficiency.toFixed(1),
          speedForMetric(bucket.avgSpeed, model.metric).toFixed(1)
        ]);
      }
    }
    blocks.push(csvBlock(typeRows));
  }

  blocks.push("");
  blocks.push(csvBlock([
    ["Goals", "Target"],
    ["Float efficiency %", model.goals.effiencyFloat],
    ["Hybrid efficiency %", model.goals.effiencyHybrid],
    ["Spin efficiency %", model.goals.effiencySpin],
    [`Float avg ${model.metric}`, speedForMetric(model.goals.avgSpeedFloat, model.metric).toFixed(1)],
    [`Hybrid avg ${model.metric}`, speedForMetric(model.goals.avgSpeedHybrid, model.metric).toFixed(1)],
    [`Spin avg ${model.metric}`, speedForMetric(model.goals.avgSpeedSpin, model.metric).toFixed(1)]
  ]));

  return blocks.join("\n");
}

function htmlTable(headers: string[], rows: Array<Array<string | number>>) {
  const head = headers.map((cell) => `<th>${cell}</th>`).join("");
  const body = rows.map((row) => `<tr>${row.map((cell) => `<td>${cell}</td>`).join("")}</tr>`).join("");
  return `<table><thead><tr>${head}</tr></thead><tbody>${body}</tbody></table>`;
}

function buildHtml(model: ReportModel) {
  const sections: string[] = [];
  sections.push(`<header><h1>ServeBoost · ${templateLabel(model.template)}</h1><p>${model.generatedAt} · ${model.period} · ${scopeLabel(model.scope)} · ${model.metric}</p></header>`);
  sections.push(`<section class="kpis"><div><span>Serves</span><strong>${model.team.totalServes.toLocaleString()}</strong></div><div><span>Efficiency</span><strong>${model.team.efficiency.toFixed(1)}%</strong></div><div><span>Avg speed</span><strong>${formatSpeed(model.team.avgSpeed, model.metric)}</strong></div><div><span>Max speed</span><strong>${formatSpeed(model.team.maxSpeed, model.metric)}</strong></div></section>`);

  sections.push("<h2>Player summary</h2>");
  sections.push(htmlTable(
    ["Player", "Pos", "Serves", "Aces", "Misses", "Eff", `Avg ${model.metric}`],
    model.stats.map((stat) => [
      stat.name,
      stat.position,
      stat.totalServes,
      stat.aces,
      stat.misses,
      `${stat.efficiency.toFixed(1)}%`,
      speedForMetric(stat.avgSpeed, model.metric).toFixed(1)
    ])
  ));

  if (model.template === "match" || model.template === "summary") {
    sections.push("<h2>Serve mix & target gaps</h2>");
    sections.push(htmlTable(
      ["Type", "Serves", "Eff", "Eff gap", `Avg ${model.metric}`, "Speed gap"],
      model.mix.map((row) => [row.label, row.total, `${row.efficiency.toFixed(1)}%`, delta(row.efficiencyGap), row.avgSpeed.toFixed(1), delta(row.speedGap)])
    ));
    sections.push("<h2>Risk bands</h2>");
    sections.push(htmlTable(
      ["Player", "Misses", "Miss rate", "Serves", "Eff"],
      model.riskBand.map((stat) => [stat.name, stat.misses, `${missRate(stat).toFixed(1)}%`, stat.totalServes, `${stat.efficiency.toFixed(1)}%`])
    ));
  }

  if (model.template === "player" || model.template === "season") {
    sections.push(`<h2>Trends vs prior (${model.previousPeriod})</h2>`);
    sections.push(htmlTable(
      ["Player", "Serves", "Eff", "Eff Δ", `Avg ${model.metric}`, "Speed Δ"],
      model.playerTrends.map((row) => [row.name, row.serves, `${row.efficiency.toFixed(1)}%`, delta(row.efficiencyDelta), row.avgSpeed.toFixed(1), delta(row.speedDelta)])
    ));
  }

  if (model.template === "player") {
    sections.push("<h2>Outcomes by serve type</h2>");
    const rows: Array<Array<string | number>> = [];
    for (const stat of model.stats) {
      for (const type of ["float", "hyb", "spin"]) {
        const bucket = stat.byType[type];
        if (!bucket?.total) continue;
        rows.push([
          stat.name,
          typeName(type),
          bucket.total,
          bucket.aces,
          bucket.misses,
          `${bucket.efficiency.toFixed(1)}%`,
          speedForMetric(bucket.avgSpeed, model.metric).toFixed(1)
        ]);
      }
    }
    sections.push(htmlTable(["Player", "Type", "Serves", "Aces", "Misses", "Eff", `Avg ${model.metric}`], rows));
  }

  if (model.template === "season") {
    sections.push("<h2>Period comparison</h2>");
    sections.push(htmlTable(
      ["Metric", "Current", "Prior", "Δ"],
      [
        ["Serves", model.team.totalServes, model.previousTeam.totalServes, model.team.totalServes - model.previousTeam.totalServes],
        ["Efficiency", `${model.team.efficiency.toFixed(1)}%`, `${model.previousTeam.efficiency.toFixed(1)}%`, delta(model.team.efficiency - model.previousTeam.efficiency)],
        [
          `Avg ${model.metric}`,
          speedForMetric(model.team.avgSpeed, model.metric).toFixed(1),
          speedForMetric(model.previousTeam.avgSpeed, model.metric).toFixed(1),
          delta(speedForMetric(model.team.avgSpeed - model.previousTeam.avgSpeed, model.metric))
        ]
      ]
    ));
    sections.push("<h2>Leaderboards</h2>");
    sections.push("<h3>Efficiency</h3>");
    sections.push(htmlTable(["Player", "Eff", "Serves"], model.leaders.efficiency.map((s) => [s.name, `${s.efficiency.toFixed(1)}%`, s.totalServes])));
    sections.push("<h3>Aces</h3>");
    sections.push(htmlTable(["Player", "Aces", "Serves"], model.leaders.aces.map((s) => [s.name, s.aces, s.totalServes])));
    sections.push(`<h3>Avg ${model.metric}</h3>`);
    sections.push(htmlTable(["Player", `Avg ${model.metric}`, "Serves"], model.leaders.speed.map((s) => [s.name, speedForMetric(s.avgSpeed, model.metric).toFixed(1), s.totalServes])));
  }

  if (model.template === "match") {
    if (model.games.length) {
      sections.push("<h2>Recent games</h2>");
      sections.push(htmlTable(
        ["Date", "Opponent", "Serves", "Ace %", "Miss %", `Pass (${model.gradeScale})`, `Avg ${model.metric}`],
        model.games.map((g) => [g.date, g.opponent, g.serves, g.aceRate.toFixed(1), g.missRate.toFixed(1), g.avgGrade, g.avgSpeed.toFixed(1)])
      ));
    }
    if (model.targets.length) {
      sections.push("<h2>Serve targets</h2>");
      sections.push(htmlTable(
        ["Jersey", `Pass (${model.gradeScale})`, "Graded", `Avg ${model.metric}`],
        model.targets.map((t) => [`#${t.number}`, t.avgGrade, t.graded, t.avgSpeed.toFixed(1)])
      ));
    } else if (!model.games.length) {
      sections.push("<p class=\"note\">No game reception data in this period — practice serve mix and risk only.</p>");
    }
  }

  sections.push("<h2>Goals</h2>");
  sections.push(`<p>Float ${model.goals.effiencyFloat}% / ${formatSpeed(model.goals.avgSpeedFloat, model.metric)} · Hybrid ${model.goals.effiencyHybrid}% / ${formatSpeed(model.goals.avgSpeedHybrid, model.metric)} · Spin ${model.goals.effiencySpin}% / ${formatSpeed(model.goals.avgSpeedSpin, model.metric)}</p>`);

  return `<!doctype html><html><head><meta charset="utf-8"><title>${model.title}</title><style>
body{font-family:Georgia,"Times New Roman",serif;background:#f4f7f5;color:#17201d;padding:32px;line-height:1.45}
h1,h2,h3{font-family:system-ui,-apple-system,sans-serif;margin:28px 0 12px}
header p,.note{color:#5b6b66}
.kpis{display:grid;grid-template-columns:repeat(4,minmax(0,1fr));gap:12px;margin:20px 0}
.kpis div{background:#fff;border:1px solid #d7e2dd;border-radius:8px;padding:14px}
.kpis span{display:block;color:#5b6b66;font-size:12px;font-family:system-ui,sans-serif}
.kpis strong{font-size:22px;font-family:system-ui,sans-serif}
table{width:100%;border-collapse:collapse;background:#fff;margin:8px 0 16px}
td,th{border-bottom:1px solid #d9e3df;padding:10px;text-align:left;font-family:system-ui,sans-serif;font-size:13px}
th{background:#eaf1ee}
@media(max-width:720px){.kpis{grid-template-columns:1fr 1fr}}
</style></head><body>${sections.join("")}</body></html>`;
}

type PdfDoc = InstanceType<typeof import("jspdf").jsPDF>;

function drawPdfRow(pdf: PdfDoc, cells: string[], widths: number[], x: number, y: number, heading = false) {
  let cursor = x;
  pdf.setFont("helvetica", heading ? "bold" : "normal");
  pdf.setFontSize(heading ? 9 : 8);
  if (heading) {
    pdf.setFillColor(234, 241, 238);
    pdf.rect(x, y - 15, widths.reduce((sum, width) => sum + width, 0), 22, "F");
  }
  pdf.setTextColor(heading ? 45 : 72, heading ? 58 : 84, heading ? 54 : 80);
  cells.forEach((cell, index) => {
    const text = pdf.splitTextToSize(cell, widths[index] - 8)[0] ?? "";
    pdf.text(text, cursor + 4, y);
    cursor += widths[index];
  });
  pdf.setDrawColor(225, 233, 229);
  pdf.line(x, y + 8, x + widths.reduce((sum, width) => sum + width, 0), y + 8);
}

async function buildPdf(model: ReportModel) {
  const { jsPDF } = await import("jspdf");
  const pdf = new jsPDF({ unit: "pt", format: "letter" });
  const margin = 44;
  let y = 50;

  const ensureSpace = (need: number) => {
    if (y + need > 740) {
      pdf.addPage();
      y = 50;
    }
  };

  const sectionTitle = (label: string) => {
    ensureSpace(40);
    y += 18;
    pdf.setFont("helvetica", "bold");
    pdf.setFontSize(13);
    pdf.setTextColor(23, 32, 29);
    pdf.text(label, margin, y);
    y += 20;
  };

  const table = (headers: string[], rows: string[][], widths: number[]) => {
    ensureSpace(40);
    drawPdfRow(pdf, headers, widths, margin, y, true);
    y += 24;
    rows.forEach((row) => {
      ensureSpace(28);
      drawPdfRow(pdf, row, widths, margin, y);
      y += 22;
    });
  };

  pdf.setFillColor(14, 17, 19);
  pdf.rect(0, 0, 612, 110, "F");
  pdf.setTextColor(243, 247, 245);
  pdf.setFont("helvetica", "bold");
  pdf.setFontSize(22);
  pdf.text(`ServeBoost · ${templateLabel(model.template)}`, margin, y);
  pdf.setFont("helvetica", "normal");
  pdf.setFontSize(10);
  pdf.setTextColor(180, 194, 190);
  pdf.text(`${model.generatedAt} · ${model.period} · ${scopeLabel(model.scope)} · ${model.metric}`, margin, y + 22);

  y = 148;
  const cards = [
    ["Tracked serves", model.team.totalServes.toLocaleString()],
    ["Efficiency", `${model.team.efficiency.toFixed(1)}%`],
    ["Average speed", formatSpeed(model.team.avgSpeed, model.metric)],
    ["Max speed", formatSpeed(model.team.maxSpeed, model.metric)]
  ];
  cards.forEach(([label, value], index) => {
    const x = margin + index * 130;
    pdf.setDrawColor(218, 228, 224);
    pdf.roundedRect(x, y, 116, 58, 5, 5);
    pdf.setTextColor(100, 116, 111);
    pdf.setFontSize(8);
    pdf.text(label, x + 10, y + 20);
    pdf.setTextColor(23, 32, 29);
    pdf.setFont("helvetica", "bold");
    pdf.setFontSize(12);
    pdf.text(value, x + 10, y + 42);
    pdf.setFont("helvetica", "normal");
  });

  y = 230;
  sectionTitle("Player summary");
  table(
    ["Player", "Pos", "Serves", "Aces", "Misses", "Eff", "Avg"],
    model.stats.map((stat) => [
      stat.name,
      stat.position,
      String(stat.totalServes),
      String(stat.aces),
      String(stat.misses),
      `${stat.efficiency.toFixed(1)}%`,
      speedForMetric(stat.avgSpeed, model.metric).toFixed(1)
    ]),
    [132, 70, 50, 44, 48, 50, 54]
  );

  if (model.template === "match" || model.template === "summary") {
    sectionTitle("Serve mix & gaps");
    table(
      ["Type", "Serves", "Eff", "Eff gap", "Avg", "Spd gap"],
      model.mix.map((row) => [row.label, String(row.total), `${row.efficiency.toFixed(1)}%`, delta(row.efficiencyGap), row.avgSpeed.toFixed(1), delta(row.speedGap)]),
      [80, 60, 60, 70, 60, 70]
    );
    sectionTitle("Risk bands");
    table(
      ["Player", "Misses", "Miss %", "Serves", "Eff"],
      model.riskBand.map((stat) => [stat.name, String(stat.misses), `${missRate(stat).toFixed(1)}%`, String(stat.totalServes), `${stat.efficiency.toFixed(1)}%`]),
      [160, 60, 60, 60, 60]
    );
  }

  if (model.template === "player" || model.template === "season") {
    sectionTitle(`Trends vs prior (${model.previousPeriod})`);
    table(
      ["Player", "Serves", "Eff", "Eff Δ", "Avg", "Spd Δ"],
      model.playerTrends.map((row) => [row.name, String(row.serves), `${row.efficiency.toFixed(1)}%`, delta(row.efficiencyDelta), row.avgSpeed.toFixed(1), delta(row.speedDelta)]),
      [140, 50, 50, 50, 50, 50]
    );
  }

  if (model.template === "player") {
    sectionTitle("Outcomes by serve type");
    const rows: string[][] = [];
    for (const stat of model.stats) {
      for (const type of ["float", "hyb", "spin"]) {
        const bucket = stat.byType[type];
        if (!bucket?.total) continue;
        rows.push([
          stat.name,
          typeName(type),
          String(bucket.total),
          String(bucket.aces),
          String(bucket.misses),
          `${bucket.efficiency.toFixed(1)}%`,
          speedForMetric(bucket.avgSpeed, model.metric).toFixed(1)
        ]);
      }
    }
    table(["Player", "Type", "Serves", "Aces", "Misses", "Eff", "Avg"], rows, [120, 60, 50, 44, 50, 50, 50]);
  }

  if (model.template === "season") {
    sectionTitle("Period comparison");
    table(
      ["Metric", "Current", "Prior", "Δ"],
      [
        ["Serves", String(model.team.totalServes), String(model.previousTeam.totalServes), String(model.team.totalServes - model.previousTeam.totalServes)],
        ["Efficiency", `${model.team.efficiency.toFixed(1)}%`, `${model.previousTeam.efficiency.toFixed(1)}%`, delta(model.team.efficiency - model.previousTeam.efficiency)],
        [
          `Avg ${model.metric}`,
          speedForMetric(model.team.avgSpeed, model.metric).toFixed(1),
          speedForMetric(model.previousTeam.avgSpeed, model.metric).toFixed(1),
          delta(speedForMetric(model.team.avgSpeed - model.previousTeam.avgSpeed, model.metric))
        ]
      ],
      [120, 120, 120, 80]
    );
    sectionTitle("Leaderboard · efficiency");
    table(
      ["Player", "Eff", "Serves"],
      model.leaders.efficiency.map((s) => [s.name, `${s.efficiency.toFixed(1)}%`, String(s.totalServes)]),
      [220, 80, 80]
    );
    sectionTitle("Leaderboard · aces");
    table(
      ["Player", "Aces", "Serves"],
      model.leaders.aces.map((s) => [s.name, String(s.aces), String(s.totalServes)]),
      [220, 80, 80]
    );
  }

  if (model.template === "match") {
    if (model.games.length) {
      sectionTitle("Recent games");
      table(
        ["Date", "Opponent", "Serves", "Ace%", "Miss%", "Pass", "Avg"],
        model.games.map((g) => [g.date, g.opponent, String(g.serves), g.aceRate.toFixed(1), g.missRate.toFixed(1), g.avgGrade, g.avgSpeed.toFixed(1)]),
        [70, 120, 50, 45, 45, 45, 45]
      );
    }
    if (model.targets.length) {
      sectionTitle("Serve targets");
      table(
        ["Jersey", "Pass avg", "Graded", "Avg spd"],
        model.targets.map((t) => [`#${t.number}`, t.avgGrade, String(t.graded), t.avgSpeed.toFixed(1)]),
        [80, 100, 80, 80]
      );
    }
  }

  sectionTitle("Goals");
  table(
    ["Float eff", "Hybrid eff", "Spin eff", "Float avg", "Hybrid avg", "Spin avg"],
    [[
      `${model.goals.effiencyFloat}%`,
      `${model.goals.effiencyHybrid}%`,
      `${model.goals.effiencySpin}%`,
      formatSpeed(model.goals.avgSpeedFloat, model.metric),
      formatSpeed(model.goals.avgSpeedHybrid, model.metric),
      formatSpeed(model.goals.avgSpeedSpin, model.metric)
    ]],
    [80, 80, 70, 90, 90, 90]
  );

  return pdf.output("blob");
}

function slugDate() {
  return new Date().toISOString().slice(0, 10);
}

function reportFilename(options: ReportOptions) {
  return `serveboost-${options.template}-${options.scope}-${slugDate()}.${options.format}`;
}

export async function generateReport(options: ReportOptions): Promise<GeneratedReport> {
  const model = buildModel(options);
  const filename = reportFilename(options);

  if (options.format === "pdf") {
    const blob = await buildPdf(model);
    return {
      filename,
      mimeType: "application/pdf",
      extension: "pdf",
      blob,
      title: model.title
    };
  }

  const text = options.format === "csv" ? buildCsv(model) : buildHtml(model);
  const mimeType = options.format === "csv" ? "text/csv;charset=utf-8" : "text/html;charset=utf-8";
  return {
    filename,
    mimeType,
    extension: options.format,
    blob: new Blob([text], { type: mimeType }),
    text,
    title: model.title
  };
}

export function downloadBlob(filename: string, blob: Blob) {
  const url = URL.createObjectURL(blob);
  const anchor = document.createElement("a");
  anchor.href = url;
  anchor.download = filename;
  anchor.click();
  URL.revokeObjectURL(url);
}

export async function blobToPayload(blob: Blob, format: ReportFormat) {
  if (format === "pdf") {
    const buffer = await blob.arrayBuffer();
    const bytes = new Uint8Array(buffer);
    let binary = "";
    bytes.forEach((byte) => {
      binary += String.fromCharCode(byte);
    });
    return `data:application/pdf;base64,${btoa(binary)}`;
  }
  return await blob.text();
}

export function payloadToBlob(payload: string, format: ReportFormat, mimeType: string) {
  if (format === "pdf") {
    const base64 = payload.includes(",") ? payload.split(",")[1] : payload;
    const binary = atob(base64);
    const bytes = new Uint8Array(binary.length);
    for (let i = 0; i < binary.length; i += 1) bytes[i] = binary.charCodeAt(i);
    return new Blob([bytes], { type: mimeType || "application/pdf" });
  }
  return new Blob([payload], { type: mimeType });
}
